// Events
import {
  COLLECTION_FILTER_OPTION_CHECK,
  COLLECTION_FILTER_OPTION_UNCHECK,
  COLLECTION_FILTER_RESET,
  COLLECTION_FILTER_SUBMIT,
  COLLECTION_FILTER_UPDATE,
  COLLECTION_SUBMIT_FILTER,
  MODAL_OPEN,
  SHOW_FIT_OPTIONS,
} from 'events';
import debounce from 'lodash/debounce';
import { component } from 'picoapp';

// Utils
import choozy from 'utils/choozy';
import { bundle, clear, hide, parseJSONScript, show, stringToNode, useTemplate } from 'utils/node';
import { dispatch } from "../../utils/theme";

export default component(async (node, ctx) => {
  const {
    checkbox: checkboxes,
    filterClear,
    filterApply,
    activeFiltersContainer,
    activeFiltersList,
    activeFilterTemplate,
    defaultSortBy,
    filterOptionsContainer = [],
  } = choozy(node);

  const form = node;

  let controller;

  const createActiveFilterElement = useTemplate(activeFilterTemplate);

  ctx.hydrate({ activeFilters: [...new FormData(form)] });

  const submit = () => {
    ctx.emit(COLLECTION_SUBMIT_FILTER, null, form);
  };

  const reset = () => {
    if (defaultSortBy) {
      defaultSortBy.checked = true;
      defaultSortBy.dispatchEvent(new Event('change', { bubbles: true }));
    }

    checkboxes.forEach((input) => {
      input.checked = false;
      input.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: {
          fromReset: true
        }
      }));
    });

    dispatch('filter-reset-all')

    if (window.location.search !== '') {
      submit();
    }
  };

  const updateAccordion = (e) => {
    const accordion = e.target.closest('.group\\/accordion');
    if (!accordion) return;

    const selectedFiltersCount = accordion.querySelector('[data-selected-filters-count]');
    const checkedInputsAmount = accordion.querySelectorAll('input:checked').length;

    if (!selectedFiltersCount) return;

    if (checkedInputsAmount === 0) {
      hide(selectedFiltersCount);
    } else {
      selectedFiltersCount.textContent = checkedInputsAmount;
      show(selectedFiltersCount);
    }
  };

  const fetchCountData = debounce(async (formData) => {
    const queryString = new URLSearchParams(formData);
    const currentParams = new URLSearchParams(window.location.search);

    if (currentParams.has('q')) {
      queryString.set('q', currentParams.get('q'));
    }

    if (currentParams.has('type')) {
      queryString.set('type', currentParams.get('type'));
    }


    queryString.set('section_id', 'template-collection-filter-count-data');

    controller = new AbortController();

    try {
      const response = await fetch(`?${queryString.toString()}`, { signal: controller.signal });
      const text = await response.text();

      const [script] = stringToNode(text, [
        '#shopify-section-template-collection-filter-count-data',
      ]);
      const { total, countData } = parseJSONScript(script[0]);

      ctx.emit(COLLECTION_FILTER_UPDATE, { countData, total });
    } catch (err) {
      if (err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, 400);

  const handleChange = (e) => {
    if (controller) controller.abort();

    const formData = new FormData(form);
    const activeFilters = [...formData]

    if (e.target && e.target.value && !e?.detail?.fromReset) {
      dispatch(e.target.checked ? 'filter-select' : 'filter-reset-option', {
        option: e.target.value,
        type: e.target.name.split('.').at(-1)
      })
    }

    ctx.emit(COLLECTION_FILTER_UPDATE, { countData: null, activeFilters });

    updateAccordion(e);
    fetchCountData(formData);
  };

  ctx.on(COLLECTION_FILTER_OPTION_UNCHECK, (state, inputId) => {
    checkboxes.forEach((checkbox) => {
      if (checkbox.id === inputId) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      }
    });
  });

  ctx.on(COLLECTION_FILTER_OPTION_CHECK, (state, inputId) => {
    checkboxes.forEach((checkbox) => {
      if (checkbox.id === inputId) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      }
    });
  });

  ctx.on(COLLECTION_FILTER_UPDATE, ({ countData, activeFilters }) => {
    const filters = activeFilters.filter(([name]) => name !== 'sort_by');

    clear(activeFiltersList);
    activeFiltersContainer.hidden = !(filters.length > 0);

    const buttons = filters.map(([name, value]) =>
      createActiveFilterElement({
        'data-filter-name': `${name}_${value}`,
        label: value,
      }),
    );

    activeFiltersList.appendChild(bundle([...buttons]));

    if (checkboxes && countData) {
      Object.keys(countData).forEach((key) => {
        const optionCheckbox = checkboxes.find((e) => e.id === key);
        if (optionCheckbox)
          optionCheckbox[+countData[key] > 0 ? 'removeAttribute' : 'setAttribute']('disabled', '');
      });
    }

    if (filterOptionsContainer) {
      [].concat(filterOptionsContainer).forEach((container) => {
        if (container.querySelectorAll('input:not(:disabled)').length > 1) {
          show(container);
        } else {
          hide(container);
        }
      });

      // Hide accordions where all the filter options containers are hidden
      [...node.querySelectorAll('.group\\/accordion')].forEach((accordion) => {
        if (accordion.contains(defaultSortBy)) return;

        if (
          accordion.querySelectorAll('[data-filter-options-container]:not([hidden])').length > 0
        ) {
          show(accordion);
        } else {
          hide(accordion);
        }
      });
    }
  });

  activeFiltersList.addEventListener('click', (e) => {
    const clickedButton = e.target.closest('button');

    if (clickedButton === null) return;

    const filterOptionId = clickedButton.dataset.filterName;

    ctx.emit(COLLECTION_FILTER_OPTION_UNCHECK, null, filterOptionId);
  });

  ctx.on(COLLECTION_FILTER_SUBMIT, submit);
  ctx.on(COLLECTION_FILTER_RESET, reset);

  node.addEventListener('change', handleChange);
  filterClear.addEventListener('click', reset);
  filterApply.addEventListener('click', submit);

  ctx.on(SHOW_FIT_OPTIONS, (state, accordionId) => {
    ctx.emit(MODAL_OPEN, null, { id: 'collection-filter-modal', props: { showFitOptions: true }});

    const firstFitInput = node.querySelector(`.group\\/accordion[data-id="${accordionId}"]`);

    if (firstFitInput) {
      const accordion = firstFitInput.closest('.group\\/accordion');
      if (accordion.classList.contains('open')) return;

      const accordionToggle = accordion.querySelector('[data-toggle]');
      accordionToggle.click();
    }
  });
});
